import type { ReactNode } from "react";
import { AlertTriangle, Inbox, Loader2, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

/** Consistent loading / error / empty states shared by every page. */

export function LoadingState({ label, hint, className = "" }: { label?: string; hint?: string; className?: string }) {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      aria-live="polite"
      className={`section-block flex flex-col items-center justify-center gap-2 px-4 py-10 text-center ${className}`}
    >
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden />
      <p className="text-sm font-medium" suppressHydrationWarning>{label ?? t("states.loading")}</p>
      <p className="text-xs text-muted-foreground" suppressHydrationWarning>{hint ?? t("states.loadingHint")}</p>
    </div>
  );
}

export function InlineLoading({ label }: { label?: string }) {
  const { t } = useTranslation();
  return (
    <span role="status" aria-live="polite" className="inline-flex items-center gap-2 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
      {label ?? t("states.loading")}
    </span>
  );
}

export function LoadingSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div role="status" aria-live="polite" className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-20 w-full animate-pulse rounded-2xl bg-muted/60" />
      ))}
    </div>
  );
}

function errorMessage(error: unknown): string | undefined {
  if (!error) return undefined;
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  return undefined;
}

export function ErrorState({
  error,
  title,
  description,
  onRetry,
  retryLabel,
  className = "",
  action,
}: {
  error?: unknown;
  title?: string;
  description?: string;
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
  action?: ReactNode;
}) {
  const { t } = useTranslation();
  const offline = typeof navigator !== "undefined" && navigator.onLine === false;
  const detail = errorMessage(error);
  return (
    <div
      role="alert"
      className={`section-block tone-peach flex flex-col items-center gap-3 px-4 py-8 text-center ${className}`}
    >
      <span className="tile-icon grid h-10 w-10 shrink-0 place-items-center">
        <AlertTriangle className="h-5 w-5 text-foreground/70" aria-hidden />
      </span>
      <div className="min-w-0">
        <p className="text-base font-semibold">{title ?? t("states.errorTitle")}</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {description ?? (offline ? t("states.offline") : t("states.errorBody"))}
        </p>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {onRetry ? (
          <Button size="sm" onClick={onRetry}>
            <RefreshCw className="mr-2 h-4 w-4" aria-hidden />
            {retryLabel ?? t("states.retry")}
          </Button>
        ) : null}
        {action}
      </div>
      {detail ? (
        <details className="w-full max-w-full text-left">
          <summary className="cursor-pointer text-xs text-muted-foreground">{t("states.details")}</summary>
          <p className="break-anywhere mt-1 text-xs text-muted-foreground">{detail}</p>
        </details>
      ) : null}
    </div>
  );
}

export function InlineError({ message, onRetry }: { message?: string; onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <div role="alert" className="flex flex-wrap items-center gap-2 rounded-xl bg-destructive/10 px-3 py-2 text-sm">
      <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" aria-hidden />
      <span className="min-w-0 flex-1">{message ?? t("states.errorBody")}</span>
      {onRetry ? (
        <Button size="sm" variant="ghost" onClick={onRetry}>
          {t("states.retry")}
        </Button>
      ) : null}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  icon,
  action,
}: {
  title?: string;
  description?: string;
  icon?: ReactNode;
  action?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="section-block flex flex-col items-center gap-2 px-4 py-8 text-center">
      <span className="tile-icon grid h-10 w-10 shrink-0 place-items-center">
        {icon ?? <Inbox className="h-5 w-5 text-foreground/70" aria-hidden />}
      </span>
      <p className="text-base font-semibold">{title ?? t("states.emptyTitle")}</p>
      <p className="text-sm text-muted-foreground">{description ?? t("states.emptyBody")}</p>
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

/** Wrap a TanStack Query result to get consistent loading/error/empty UI. */
export function QueryState({
  query,
  children,
  loading,
  empty,
  isEmpty,
}: {
  query: { isPending?: boolean; isLoading?: boolean; isError?: boolean; error?: unknown; refetch?: () => unknown };
  children: ReactNode;
  loading?: ReactNode;
  empty?: ReactNode;
  isEmpty?: boolean;
}) {
  if (query.isPending ?? query.isLoading) return <>{loading ?? <LoadingState />}</>;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch?.()} />;
  if (isEmpty) return <>{empty ?? <EmptyState />}</>;
  return <>{children}</>;
}
